import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import Select from "react-select";
import {
  Search,
  Building,
  User,
  Mail,
  Calendar,
  MapPin,
  CreditCard,
  UserPlus,
  Trash2,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  Loader2,
  Shield,
  ChevronUp,
  Home,
} from "lucide-react";
import connectionService from "../services/connectionService";
import companiesHouseService from "../services/companiesHouseService";
import TeamService from "../services/teamService";
import bankValidationService from "../services/bankValidationService";

// ── PartnerCard — defined OUTSIDE CustomerDetails so React never remounts it ──
const PartnerCard = React.memo(({ partner, index, onChange, onRemove, canRemove, readOnly }) => {
  const [collapsed, setCollapsed] = useState(false);

  // Uses (index, field, value) — matches handlePartnerChange in parent
  const handle = useCallback((field, value) => {
    onChange(index, field, value);
  }, [index, onChange]);

  if (readOnly) {
    return (
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div
          className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer select-none"
          onClick={() => setCollapsed((c) => !c)}
        >
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-semibold">
              {index + 1}
            </div>
            <span className="font-medium text-gray-800 text-sm">
              {partner.name?.trim() || `Partner / Director ${index + 1}`}
            </span>
            {partner.position && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                {partner.position}
              </span>
            )}
          </div>
          {collapsed ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronUp className="w-4 h-4 text-gray-400" />}
        </div>
        {!collapsed && (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-white">
            <div className="flex items-start gap-2">
              <User className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Full Name</p>
                <p className="text-sm font-medium text-gray-800">{partner.name || "—"}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <User className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Position / Role</p>
                <p className="text-sm font-medium text-gray-800">{partner.position || "—"}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Calendar className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Date of Birth</p>
                <p className="text-sm font-medium text-gray-800">
                  {partner.dob
                    ? new Date(partner.dob.toString().slice(0, 10) + "T12:00:00").toLocaleDateString("en-GB")
                    : "—"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Mail className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Email Address</p>
                <p className="text-sm font-medium text-gray-800">{partner.email || "—"}</p>
              </div>
            </div>
            <div className="md:col-span-2 flex items-start gap-2">
              <Home className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Private Address</p>
                <p className="text-sm font-medium text-gray-800 whitespace-pre-line">{partner.private_address || "—"}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Postcode</p>
                <p className="text-sm font-medium text-gray-800">{partner.postcode || "—"}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div
        className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer select-none"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-semibold">
            {index + 1}
          </div>
          <span className="font-medium text-gray-800 text-sm">
            {partner.name?.trim() || `Partner / Director ${index + 1}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {canRemove && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRemove(index); }}
              className="text-red-500 hover:text-red-700 transition-colors p-1 rounded"
              title="Remove partner"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {collapsed ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronUp className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {!collapsed && (
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input
              type="text"
              value={partner.name || ""}
              onChange={(e) => handle("name", e.target.value)}
              placeholder="Full legal name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Position / Role</label>
            <input
              type="text"
              value={partner.position || ""}
              onChange={(e) => handle("position", e.target.value)}
              placeholder="e.g. Director, Partner"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
            <input
              type="date"
              value={partner.dob ? partner.dob.toString().slice(0, 10) : ""}
              onChange={(e) => handle("dob", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input
              type="email"
              value={partner.email || ""}
              onChange={(e) => handle("email", e.target.value)}
              placeholder="partner@email.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Private Address *</label>
            <textarea
              value={partner.private_address || ""}
              onChange={(e) => handle("private_address", e.target.value)}
              rows={3}
              placeholder="Full home / private address"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
            <input
              type="text"
              value={partner.postcode || ""}
              onChange={(e) => handle("postcode", e.target.value)}
              placeholder="e.g. SS1 1AA"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      )}
    </div>
  );
});

// ── Blank partner factory ─────────────────────────────────────────────────────
const blankPartner = () => ({
  name: "", position: "", dob: "", email: "", private_address: "", postcode: "",
});

// ── Main component ────────────────────────────────────────────────────────────
const CustomerDetails = forwardRef(
  ({ onConnectionSaved, initialData, isEditMode = false }, ref) => {
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [searchTerms, setSearchTerms] = useState({});
    const [dropdownOpen, setDropdownOpen] = useState({});
    const dropdownRefs = useRef({});
    const [serviceTypes, setServiceTypes] = useState([]);
    const [numberTypes, setNumberTypes] = useState([]);
    const [customDescriptions, setCustomDescriptions] = useState({});
    const [showAddForm, setShowAddForm] = useState({});
    const [newEntryForm, setNewEntryForm] = useState({});
    const [isCreating, setIsCreating] = useState({});
    const [companySearchTerm, setCompanySearchTerm] = useState("");
    const [companySearchResults, setCompanySearchResults] = useState([]);
    const [isSearchingCompany, setIsSearchingCompany] = useState(false);
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [companySearchMode, setCompanySearchMode] = useState("name");
    const companySearchRef = useRef(null);
    const [teamMembers, setTeamMembers] = useState([]);
    const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
    const [isEditing, setIsEditing] = useState(isEditMode || !!initialData?.id);

    const userId = localStorage.getItem("userId");
    const userName = localStorage.getItem("userName");

    // ── formData ──────────────────────────────────────────────────────────────
    const [formData, setFormData] = useState(
      initialData
        ? {
            ...initialData,
            partners: initialData.partners?.length ? initialData.partners : [blankPartner()],
          }
        : {
            salesman_name: userName ?? "",
            salesman_id: userId ?? "",
            regulation_status: "LIMITED",
            installation_address: "",
            contract_date: new Date().toISOString().split("T")[0],
            company_reg: "",
            md_name: "",
            position: "",
            company_name: "",
            registered_address_line_1: "",
            registered_address_line_2: "",
            registered_address_line_3: "",
            registered_address_line_4: "",
            postcode: "",
            director_email: "",
            billing_email: "",
            bank_name: "",
            bank_address: "",
            bank_postcode: "",
            account_holder: "",
            account_number: "",
            sort_code: "",
            isdn2e_channels_required: 0,
            isdn30e_channels_required: 0,
            analogue_channels_required: 0,
            partners: [blankPartner()],
            services: [{
              id: 1, description: "", number_type_id: 1, connection_option_id: 1,
              existing_tel_no: "", existing_lines: "", postcode_of_homeworker: "",
            }],
          },
    );

    // ── Effects ───────────────────────────────────────────────────────────────
    useEffect(() => {
      const fetchData = async () => {
        try {
          const [serviceRes, numberRes] = await Promise.all([
            connectionService.getServiceTypes(),
            connectionService.getNumberTypes(),
          ]);
          setServiceTypes(serviceRes.data.map((item) => ({ value: item.id, label: item.name })));
          setNumberTypes(numberRes.data.map((item) => ({ value: item.id, label: item.name })));
        } catch (error) {
          console.error("Failed to load service or number types", error);
        }
      };
      fetchData();
    }, []);

    useEffect(() => {
      if (userId) {
        setFormData((prev) => ({ ...prev, salesman_id: userId, salesman_name: userName }));
      }
    }, [userId, userName]);

    useEffect(() => {
      const fetchTeamMembers = async () => {
        setLoadingTeamMembers(true);
        try {
          const response = await TeamService.getTeamMembers();
          setTeamMembers(response.data);
        } catch (err) {
          setErrors({ teamMembers: err.message || "Failed to load team members" });
        } finally {
          setLoadingTeamMembers(false);
        }
      };
      fetchTeamMembers();
    }, [userId, userName]);

    useEffect(() => {
      if (initialData) {
        setFormData({
          ...initialData,
          partners: initialData.partners?.length ? initialData.partners : [blankPartner()],
        });
        if (initialData.id) setIsEditing(true);
      }
    }, [initialData]);

    // ── Partner handlers — stable with useCallback ────────────────────────────
    // Signature: (index, field, value) — matches PartnerCard's handle()
    const handlePartnerChange = useCallback((index, field, value) => {
      setFormData((prev) => {
        const partners = [...prev.partners];
        partners[index] = { ...partners[index], [field]: value };
        return { ...prev, partners };
      });
    }, []); // empty deps — fully stable, never recreated

    const addPartner = useCallback(() => {
      setFormData((prev) => ({ ...prev, partners: [...prev.partners, blankPartner()] }));
    }, []);

    const removePartner = useCallback((index) => {
      setFormData((prev) => ({
        ...prev,
        partners: prev.partners.filter((_, i) => i !== index),
      }));
    }, []);

    // ── Bank validation ───────────────────────────────────────────────────────
    const [bankValidation, setBankValidation] = useState({
      sortCode: { isValid: null, loading: false, data: null, error: null },
      accountNumber: { isValid: null, loading: false, data: null, error: null },
      combinedValidation: { isValid: null, loading: false, data: null, error: null },
    });
    const [validationDebounce, setValidationDebounce] = useState(null);

    const validateBankAccountWithAPI = async (sortCode, accountNumber) => {
      const cleanSortCode = sortCode.replace(/\D/g, "");
      const cleanAccountNumber = accountNumber.replace(/\D/g, "");
      if (cleanSortCode.length !== 6 || cleanAccountNumber.length < 6 || cleanAccountNumber.length > 8) return;

      setBankValidation((prev) => ({
        ...prev,
        combinedValidation: { ...prev.combinedValidation, loading: true, error: null },
      }));
      try {
        const response = await bankValidationService.validateBankAccount(cleanSortCode, cleanAccountNumber);
        setBankValidation((prev) => ({
          ...prev,
          combinedValidation: { isValid: response.valid, loading: false, data: response.data, error: response.error },
        }));
        if (response.valid && response.data) {
          const bankName = response.data.bank || response.data.bankname;
          if (bankName && !formData.bank_name) {
            setFormData((prev) => ({ ...prev, bank_name: bankName }));
          }
        }
      } catch (error) {
        setBankValidation((prev) => ({
          ...prev,
          combinedValidation: { isValid: false, loading: false, data: null, error: error.message || "Validation service unavailable" },
        }));
      }
    };

    useEffect(() => {
      if (validationDebounce) clearTimeout(validationDebounce);
      const cleanSortCode = formData.sort_code?.replace(/\D/g, "") || "";
      const cleanAccountNumber = formData.account_number?.replace(/\D/g, "") || "";
      if (cleanSortCode.length === 6 && cleanAccountNumber.length >= 6 && cleanAccountNumber.length <= 8) {
        const timer = setTimeout(() => {
          validateBankAccountWithAPI(cleanSortCode, cleanAccountNumber);
        }, 1000);
        setValidationDebounce(timer);
      }
      return () => { if (validationDebounce) clearTimeout(validationDebounce); };
    }, [formData.sort_code, formData.account_number]);

    // ── Validation helpers ────────────────────────────────────────────────────
    const validateSortCode = (sortCode) => {
      if (!sortCode) return { isValid: false, message: "Sort code is required" };
      const clean = sortCode.replace(/\D/g, "");
      if (clean.length !== 6) return { isValid: false, message: "Sort code must be 6 digits" };
      return { isValid: true, message: "" };
    };

    const validateAccountNumber = (accountNumber) => {
      if (!accountNumber) return { isValid: false, message: "Account number is required" };
      const clean = accountNumber.replace(/\D/g, "");
      if (clean.length < 6 || clean.length > 8) return { isValid: false, message: "Account number must be between 6-8 digits" };
      return { isValid: true, message: "" };
    };

    const formatSortCode = (sortCode) => {
      const clean = sortCode.replace(/\D/g, "");
      return clean.length === 6 ? clean.replace(/(\d{2})(\d{2})(\d{2})/, "$1-$2-$3") : clean;
    };

    const handleSortCodeChange = (value) => {
      const raw = value.replace(/\D/g, "");
      const validation = validateSortCode(raw);
      if (!validation.isValid && raw) setErrors((prev) => ({ ...prev, sort_code: validation.message }));
      else if (errors.sort_code) setErrors((prev) => ({ ...prev, sort_code: "" }));
      setFormData((prev) => ({ ...prev, sort_code: formatSortCode(value) }));
      setBankValidation((prev) => ({
        ...prev,
        combinedValidation: { isValid: null, loading: false, data: null, error: null },
      }));
    };

    const handleAccountNumberChange = (value) => {
      const clean = value.replace(/\D/g, "");
      const validation = validateAccountNumber(clean);
      if (!validation.isValid && clean) setErrors((prev) => ({ ...prev, account_number: validation.message }));
      else if (errors.account_number) setErrors((prev) => ({ ...prev, account_number: "" }));
      setFormData((prev) => ({ ...prev, account_number: clean }));
      setBankValidation((prev) => ({
        ...prev,
        combinedValidation: { isValid: null, loading: false, data: null, error: null },
      }));
    };

    // ── Input change ──────────────────────────────────────────────────────────
    const handleInputChange = (field, value) => {
      if (field === "sort_code") { handleSortCodeChange(value); return; }
      if (field === "account_number") { handleAccountNumberChange(value); return; }

      const updated = { ...formData, [field]: value };

      if (["registered_address_line_1","registered_address_line_2","registered_address_line_3","registered_address_line_4","postcode"].includes(field)) {
        updated.installation_address = [
          field === "registered_address_line_1" ? value : updated.registered_address_line_1,
          field === "registered_address_line_2" ? value : updated.registered_address_line_2,
          field === "registered_address_line_3" ? value : updated.registered_address_line_3,
          field === "registered_address_line_4" ? value : updated.registered_address_line_4,
          field === "postcode" ? value : updated.postcode,
        ].filter((l) => l && l.trim() !== "").join(", ");
      }

      setFormData(updated);
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
    };

    // ── Companies House ───────────────────────────────────────────────────────
    const statusStyles = {
      active: "bg-green-100 text-green-800",
      dissolved: "bg-gray-100 text-gray-800",
      overdue: "bg-red-100 text-red-800",
      default: "bg-blue-100 text-blue-800",
    };

    const getStatusStyle = (status) => {
      if (!status) return statusStyles.default;
      if (status.toLowerCase().includes("overdue")) return statusStyles.overdue;
      if (status.toLowerCase().includes("dissolved")) return statusStyles.dissolved;
      if (status.toLowerCase().includes("active")) return statusStyles.active;
      return statusStyles.default;
    };

    const searchCompaniesByName = async (searchTerm) => {
      if (!searchTerm || searchTerm.length < 2) { setCompanySearchResults([]); return; }
      setIsSearchingCompany(true);
      try {
        const response = await companiesHouseService.searchCompanies(searchTerm, "name");
        if (response.items) { setCompanySearchResults(response.items); setShowCompanyDropdown(true); }
        else setCompanySearchResults([]);
      } catch (error) {
        showNotification("Failed to search companies", "error");
        setCompanySearchResults([]);
      } finally {
        setIsSearchingCompany(false);
      }
    };

    const searchCompanyByNumber = async (companyNumber) => {
      if (!companyNumber || companyNumber.length < 3) return;
      setIsSearchingCompany(true);
      try {
        const response = await companiesHouseService.getCompanyDetails(companyNumber);
        setFormData((prev) => ({ ...prev, ...response }));
        showNotification("Company details loaded successfully", "success");
        setShowCompanyDropdown(false);
        setCompanySearchTerm("");
      } catch (error) {
        showNotification(
          error.message === "Company not found" ? "Company not found with this registration number" : "Failed to fetch company details",
          "error"
        );
      } finally {
        setIsSearchingCompany(false);
      }
    };

    const selectCompany = async (company) => {
      setIsSearchingCompany(true);
      try {
        const response = await companiesHouseService.getCompanyDetails(company.company_number);
        setFormData((prev) => ({ ...prev, ...response }));
        showNotification("Company details loaded successfully", "success");
        setShowCompanyDropdown(false);
        setCompanySearchTerm("");
      } catch (error) {
        showNotification("Failed to fetch company details", "error");
      } finally {
        setIsSearchingCompany(false);
      }
    };

    useEffect(() => {
      const t = setTimeout(() => {
        if (companySearchMode === "name" && companySearchTerm) searchCompaniesByName(companySearchTerm);
        else if (companySearchMode === "number" && companySearchTerm) searchCompanyByNumber(companySearchTerm);
      }, 500);
      return () => clearTimeout(t);
    }, [companySearchTerm, companySearchMode]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (companySearchRef.current && !companySearchRef.current.contains(event.target))
          setShowCompanyDropdown(false);
        Object.keys(dropdownRefs.current).forEach((index) => {
          if (dropdownRefs.current[index] && !dropdownRefs.current[index].contains(event.target)) {
            setDropdownOpen((prev) => ({ ...prev, [index]: false }));
            setShowAddForm((prev) => ({ ...prev, [index]: false }));
          }
        });
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // ── Salesman ──────────────────────────────────────────────────────────────
    const handleSalesmanChange = (selectedOption) => {
      if (selectedOption) {
        setFormData((prev) => ({ ...prev, salesman_id: selectedOption.value, salesman_name: selectedOption.name }));
      }
    };

    const getSalesmanOptions = () => {
      const options = [];
      if (userId) options.push({ value: userId, label: `${userName} (You)`, name: userName, isCurrentUser: true });
      teamMembers
        .filter((member) => member.id !== userId)
        .forEach((member) => options.push({ value: member.id, label: member.name, name: member.name, isCurrentUser: false }));
      return options;
    };

    const getCurrentSalesmanValue = () => {
      const options = getSalesmanOptions();
      return options.find((option) => option.value === formData.salesman_id) || null;
    };

    // ── Form validation ───────────────────────────────────────────────────────
    const validateForm = () => {
      const newErrors = {};
      if (!formData.salesman_name?.trim()) newErrors.salesman_name = "Salesman name is required";
      if (!formData.company_name?.trim()) newErrors.company_name = "Company name is required";
      if (!formData.director_email?.trim()) newErrors.director_email = "Director email is required";

      const sortCodeValidation = validateSortCode(formData.sort_code?.replace(/\D/g, ""));
      if (!sortCodeValidation.isValid && formData.sort_code) newErrors.sort_code = sortCodeValidation.message;

      const accountNumberValidation = validateAccountNumber(formData.account_number);
      if (!accountNumberValidation.isValid && formData.account_number) newErrors.account_number = accountNumberValidation.message;

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (formData.director_email && !emailRegex.test(formData.director_email)) newErrors.director_email = "Invalid email format";
      if (formData.billing_email && !emailRegex.test(formData.billing_email)) newErrors.billing_email = "Invalid email format";

      if (formData.regulation_status === "NON LIMITED") {
        formData.partners.forEach((p, i) => {
          if (!p.name?.trim()) newErrors[`partner_${i}_name`] = "Partner name is required";
          if (!p.dob) newErrors[`partner_${i}_dob`] = "Date of birth is required";
          if (!p.private_address?.trim()) newErrors[`partner_${i}_address`] = "Private address is required";
        });
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const showNotification = (message, type = "success") => {
      setNotification({ message, type });
      setTimeout(() => setNotification(null), 3000);
    };

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSaveConnection = async () => {
      if (!validateForm()) return;
      setLoading(true);
      try {
        const transformedServices = (formData.services || []).map((service) => {
          if (service.description === "__custom__") {
            return { ...service, description: service.customDescription, number_type_id: null };
          }
          const selected = numberTypes.find((opt) => opt.label === service.description);
          return {
            ...service,
            description: selected?.label || service.description,
            number_type_id: selected?.value || service.number_type_id,
          };
        });

        const payload = {
          ...formData,
          services: transformedServices,
          partners: formData.regulation_status === "NON LIMITED" ? formData.partners : [],
          regulation_status: formData.regulation_status,
        };
        if (!payload.id) delete payload.id;

        let response;
        let connectionId;

        if (isEditing && formData.id) {
          response = await connectionService.updateConnection(formData.id, payload);
          connectionId = formData.id;
          showNotification("Connection updated successfully");
        } else {
          response = await connectionService.createConnection(payload);
          connectionId = response.data.id;
          showNotification("Connection created successfully");
        }

        if (onConnectionSaved) onConnectionSaved(connectionId, payload);
        return connectionId;
      } catch (error) {
        console.error("Failed to save connection:", error);
        if (error.response) console.error("Error response:", error.response.data);
        showNotification("Failed to save connection", "error");
      } finally {
        setLoading(false);
      }
    };

    useImperativeHandle(ref, () => ({ handleSaveConnection }));

    // ── Service helpers ───────────────────────────────────────────────────────
    const handleServiceChange = (index, field, value) => {
      const updatedServices = [...formData.services];
      updatedServices[index] = { ...updatedServices[index], [field]: value };
      setFormData((prev) => ({ ...prev, services: updatedServices }));
    };

    const addService = () => {
      setFormData((prev) => ({
        ...prev,
        services: [...prev.services, {
          id: Date.now(), description: "", number_type_id: 1, connection_option_id: 1,
          existing_tel_no: "", existing_lines: "", postcode_of_homeworker: "",
        }],
      }));
    };

    const removeService = (index) => {
      if (formData.services.length > 1) {
        setFormData((prev) => ({ ...prev, services: prev.services.filter((_, i) => i !== index) }));
      }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
      <div className="max-w-7xl mx-auto">
        {notification && (
          <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium
            ${notification.type === "error" ? "bg-red-600 text-white" : "bg-green-600 text-white"}`}>
            {notification.type === "error" ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
            {notification.message}
          </div>
        )}

        <form className="space-y-6">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-8 space-y-8">

              {/* Companies House Search */}
              <div className="bg-blue-50 rounded-lg p-6 space-y-4">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Building className="w-5 h-5 text-blue-600" />
                  Search Companies House
                </h2>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input type="radio" value="name" checked={companySearchMode === "name"}
                        onChange={(e) => { setCompanySearchMode(e.target.value); setCompanySearchTerm(""); setCompanySearchResults([]); }}
                        className="mr-2" />
                      Search by Company Name
                    </label>
                    <label className="flex items-center">
                      <input type="radio" value="number" checked={companySearchMode === "number"}
                        onChange={(e) => { setCompanySearchMode(e.target.value); setCompanySearchTerm(""); setCompanySearchResults([]); }}
                        className="mr-2" />
                      Search by Registration Number
                    </label>
                  </div>
                  <div className="relative" ref={companySearchRef}>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input type="text" value={companySearchTerm}
                        onChange={(e) => setCompanySearchTerm(e.target.value)}
                        onKeyPress={(e) => { if (e.key === "Enter" && companySearchMode === "number") { e.preventDefault(); searchCompanyByNumber(companySearchTerm); } }}
                        placeholder={companySearchMode === "name" ? "Start typing company name..." : "Enter company registration number (e.g., 12345678)"}
                        className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      {isSearchingCompany && <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-600 animate-spin" />}
                    </div>
                    {companySearchMode === "number" && (
                      <button type="button" onClick={() => searchCompanyByNumber(companySearchTerm)}
                        disabled={!companySearchTerm || isSearchingCompany}
                        className="mt-2 w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors">
                        {isSearchingCompany ? <><Loader2 className="w-4 h-4 animate-spin" />Searching...</> : <><Search className="w-4 h-4" />Search Company</>}
                      </button>
                    )}
                    {showCompanyDropdown && companySearchResults.length > 0 && companySearchMode === "name" && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {companySearchResults.map((company, index) => (
                          <div key={index} onClick={() => selectCompany(company)}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                            <div className="font-medium text-gray-900">{company.title}</div>
                            <div className="text-sm text-gray-600">{company.company_number} • {company.company_status} • {company.address_snippet}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>• Search by company name will show suggestions as you type</p>
                    <p>• Search by registration number requires the exact number (8 digits for UK companies)</p>
                    <p>• Company details will auto-fill the form fields below</p>
                  </div>
                </div>
              </div>

              {/* Basic Information */}
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2 border-b pb-2">
                  <Building className="w-5 h-5 text-blue-600" />
                  Company Details
                </h2>

                {formData.company_status && (
                  <div className={`p-4 rounded-lg border ${getStatusStyle(formData.company_status)}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Company Status:</span>
                        <span className="font-semibold capitalize">{formData.company_status.toLowerCase()}</span>
                      </div>
                      {formData.company_status.toLowerCase() === "dissolved" && (
                        <div className="flex items-center gap-2 text-red-600 font-medium">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-sm">This company has been dissolved</span>
                        </div>
                      )}
                    </div>
                    {formData.last_filing_date && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <span className="text-sm text-gray-600">Last filing: <span className="font-medium text-gray-900">{new Date(formData.last_filing_date).toLocaleDateString()}</span></span>
                      </div>
                    )}
                  </div>
                )}

                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2 border-b pb-2">
                  <User className="w-5 h-5 text-blue-600" />
                  Basic Information
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Salesman */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Salesman Name *</label>
                    {loadingTeamMembers ? (
                      <div className="animate-pulse h-12 bg-gray-200 rounded-lg"></div>
                    ) : (
                      <Select
                        options={getSalesmanOptions()}
                        value={getCurrentSalesmanValue()}
                        onChange={handleSalesmanChange}
                        placeholder="Search and select salesman..."
                        isSearchable
                        isClearable={false}
                        noOptionsMessage={() => "No salesmen found"}
                        formatOptionLabel={(option) => (
                          <div className="flex items-center justify-between">
                            <span>{option.label}</span>
                            {option.isCurrentUser && <span className="text-xs text-blue-600 font-medium">YOU</span>}
                          </div>
                        )}
                        styles={{
                          control: (base, state) => ({
                            ...base, minHeight: "48px",
                            borderColor: errors.salesman_name ? "#ef4444" : state.isFocused ? "#3b82f6" : "#d1d5db",
                            boxShadow: state.isFocused ? (errors.salesman_name ? "0 0 0 2px rgba(239,68,68,0.2)" : "0 0 0 2px rgba(59,130,246,0.2)") : "none",
                            "&:hover": { borderColor: errors.salesman_name ? "#ef4444" : "#9ca3af" },
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected ? "#3b82f6" : state.isFocused ? "#eff6ff" : "white",
                            color: state.isSelected ? "white" : "#374151",
                          }),
                        }}
                      />
                    )}
                    {errors.salesman_name && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{errors.salesman_name}</p>
                    )}
                  </div>

                  {/* Regulation Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Regulation Status</label>
                    <select value={formData.regulation_status}
                      onChange={(e) => handleInputChange("regulation_status", e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option value="LIMITED">Limited</option>
                      <option value="NON LIMITED">Non-Limited</option>
                    </select>
                  </div>

                  {/* Partners section — only shown for NON LIMITED */}
                  {formData.regulation_status === "NON LIMITED" && (
                    <div className="md:col-span-2 space-y-4">
                      <div className="flex items-center justify-between border-b pb-2">
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                          <Shield className="w-5 h-5 text-orange-500" />
                          Partners / Directors
                          <span className="ml-2 px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded-full font-medium">Non-Limited</span>
                        </h2>
                        <button type="button" onClick={addPartner}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                          <UserPlus className="w-4 h-4" />
                          Add Partner
                        </button>
                      </div>

                      <p className="text-sm text-gray-500">
                        Add details for each partner or director. At least one is required for Non-Limited connections.
                      </p>

                      <div className="space-y-3">
                        {formData.partners.map((partner, index) => (
                          <div key={index}>
                            <PartnerCard
                              partner={partner}
                              index={index}
                              onChange={handlePartnerChange}
                              onRemove={removePartner}
                              canRemove={formData.partners.length > 1}
                              readOnly={false}
                            />
                            {(errors[`partner_${index}_name`] || errors[`partner_${index}_dob`] || errors[`partner_${index}_address`]) && (
                              <div className="mt-1 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 space-y-1">
                                {errors[`partner_${index}_name`]    && <p className="flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors[`partner_${index}_name`]}</p>}
                                {errors[`partner_${index}_dob`]     && <p className="flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors[`partner_${index}_dob`]}</p>}
                                {errors[`partner_${index}_address`] && <p className="flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors[`partner_${index}_address`]}</p>}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      <button type="button" onClick={addPartner}
                        className="w-full py-3 border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 rounded-xl text-sm text-gray-500 hover:text-blue-600 flex items-center justify-center gap-2 transition-all">
                        <UserPlus className="w-4 h-4" />
                        Add another partner / director
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                    <input type="date" value={formData.contract_date}
                      onChange={(e) => handleInputChange("contract_date", e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company Registration</label>
                    <input type="text" value={formData.company_reg}
                      onChange={(e) => handleInputChange("company_reg", e.target.value)}
                      placeholder="Company registration number"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                </div>
              </div>

              {/* Company Details */}
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2 border-b pb-2">
                  <Building className="w-5 h-5 text-blue-600" />
                  Company Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                    <input type="text" value={formData.company_name}
                      onChange={(e) => handleInputChange("company_name", e.target.value)}
                      readOnly={formData.regulation_status === "LIMITED"}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errors.company_name ? "border-red-500" : "border-gray-300"} ${formData.regulation_status === "LIMITED" ? "bg-gray-100 cursor-not-allowed" : ""}`}
                      placeholder="Enter company name" />
                    {errors.company_name && <p className="text-red-500 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{errors.company_name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">MD Name</label>
                    <input type="text" value={formData.md_name}
                      onChange={(e) => handleInputChange("md_name", e.target.value)}
                      placeholder="Managing Director name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
                    <input type="text" value={formData.position}
                      onChange={(e) => handleInputChange("position", e.target.value)}
                      placeholder="Your position"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Postcode</label>
                    <input type="text" value={formData.postcode}
                      onChange={(e) => handleInputChange("postcode", e.target.value)}
                      placeholder="Postcode"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Registered Address</label>
                    <div className="space-y-3">
                      {["registered_address_line_1","registered_address_line_2","registered_address_line_3","registered_address_line_4"].map((field, index) => (
                        <input key={field} type="text" value={formData[field] || ""}
                          onChange={(e) => handleInputChange(field, e.target.value)}
                          readOnly={formData.regulation_status === "LIMITED"}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${formData.regulation_status === "LIMITED" ? "bg-gray-100 cursor-not-allowed border-gray-300" : "border-gray-300"}`}
                          placeholder={`Address Line ${index + 1}`} />
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Installation Address</label>
                    <textarea value={formData.installation_address || ""}
                      onChange={(e) => handleInputChange("installation_address", e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Full installation address" />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2 border-b pb-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Director's Email *</label>
                    <input type="email" value={formData.director_email}
                      onChange={(e) => handleInputChange("director_email", e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errors.director_email ? "border-red-500" : "border-gray-300"}`}
                      placeholder="director@company.com" />
                    {errors.director_email && <p className="text-red-500 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{errors.director_email}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Billing Email</label>
                    <input type="email" value={formData.billing_email}
                      onChange={(e) => handleInputChange("billing_email", e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errors.billing_email ? "border-red-500" : "border-gray-300"}`}
                      placeholder="billing@company.com" />
                    {errors.billing_email && <p className="text-red-500 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{errors.billing_email}</p>}
                  </div>
                </div>
              </div>

              {/* Banking Details */}
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2 border-b pb-2">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  Banking Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                    <input type="text" value={formData.bank_name}
                      onChange={(e) => handleInputChange("bank_name", e.target.value)}
                      placeholder="Bank name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Account Holder</label>
                    <input type="text" value={formData.account_holder}
                      onChange={(e) => handleInputChange("account_holder", e.target.value)}
                      placeholder="Account holder name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>

                  {/* Account Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                    <input type="text" value={formData.account_number}
                      onChange={(e) => handleAccountNumberChange(e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errors.account_number ? "border-red-500" : bankValidation.combinedValidation.isValid === false ? "border-red-500" : bankValidation.combinedValidation.isValid === true ? "border-green-500" : "border-gray-300"}`}
                      placeholder="6-8 digits" maxLength="8" />
                    {errors.account_number && <p className="text-red-500 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{errors.account_number}</p>}
                    {bankValidation.combinedValidation.loading && <p className="text-blue-600 text-sm mt-1 flex items-center gap-1"><Loader2 className="w-4 h-4 animate-spin" />Validating bank account...</p>}
                    {bankValidation.combinedValidation.isValid === true && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-700 text-sm font-medium flex items-center gap-2"><CheckCircle className="w-4 h-4" />Bank Account Validated Successfully</p>
                        {bankValidation.combinedValidation.data?.bank && <p className="text-green-600 text-sm ml-6"><span className="font-medium">Bank:</span> {bankValidation.combinedValidation.data.bank}</p>}
                        {bankValidation.combinedValidation.data?.bic  && <p className="text-green-600 text-sm ml-6"><span className="font-medium">BIC:</span> {bankValidation.combinedValidation.data.bic}</p>}
                      </div>
                    )}
                    {bankValidation.combinedValidation.isValid === false && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-700 text-sm font-medium flex items-center gap-2"><AlertCircle className="w-4 h-4" />Invalid Bank Account Details</p>
                        {bankValidation.combinedValidation.error && <p className="text-red-600 text-sm mt-1 ml-6">{bankValidation.combinedValidation.error}</p>}
                      </div>
                    )}
                  </div>

                  {/* Sort Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sort Code</label>
                    <input type="text" value={formData.sort_code}
                      onChange={(e) => handleSortCodeChange(e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errors.sort_code ? "border-red-500" : bankValidation.sortCode.isValid === true ? "border-green-500" : bankValidation.sortCode.isValid === false ? "border-red-500" : "border-gray-300"}`}
                      placeholder="XX-XX-XX format" maxLength="8" />
                    {errors.sort_code && <p className="text-red-500 text-sm mt-1 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{errors.sort_code}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bank Address & Postcode</label>
                  <div className="space-y-3">
                    <input type="text" value={formData.bank_address}
                      onChange={(e) => handleInputChange("bank_address", e.target.value)}
                      placeholder="Bank address"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    <input type="text" value={formData.bank_postcode}
                      onChange={(e) => handleInputChange("bank_postcode", e.target.value)}
                      placeholder="Bank postcode"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button type="button" className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="button" onClick={handleSaveConnection} disabled={loading}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 disabled:opacity-50">
              {loading ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div><span>{isEditing ? "Updating..." : "Saving..."}</span></>
              ) : (
                <><CheckCircle className="w-5 h-5" /><span>{isEditing ? "Update Connection" : "Save Connection"}</span></>
              )}
            </button>
          </div>
        </form>
      </div>
    );
  },
);

export default CustomerDetails;